import { getPrescriptionUrl } from '@/api/prescriptions/getPrescriptionUrl';
import { getRefillPrescriptionUrl } from '@/api/prescriptions/getRefillPrescriptionUrl';
import type { PrescriptionItem } from '@/types';
import { toast } from '@montugroup/design-system';
import { PatientPrescriptionStatus } from '@montugroup/pms-api-contracts';
import type { ChipProps } from '@mui/material';
import { Box, Button, Chip, Paper, Skeleton, Tooltip, Typography, useTheme } from '@mui/material';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { PrescriptionMedicationsTable } from './PrescriptionMedicationsTable';
import type { CancelProductCallback, CancelProductPredicate } from './PrescriptionMedicationsTable.types';

const statusColorMap: Record<PatientPrescriptionStatus, ChipProps['color']> = {
  Active: 'success',
  Cancelled: 'error',
  Deleted: 'error',
  Expired: 'default',
  Error: 'error',
};

const flexColumnToRow = {
  display: 'flex',
  alignItems: { xs: 'start', lg: 'center' },
  flexDirection: { xs: 'column', lg: 'row' },
};

interface PrescriptionProps {
  prescription?: PrescriptionItem;
  isLoading?: boolean;
  onCancelProduct?: CancelProductCallback;
  isProductCancellable?: CancelProductPredicate;
  hasCancelPermissions?: boolean;
}

export const Prescription = ({
  prescription,
  isLoading,
  onCancelProduct,
  isProductCancellable,
  hasCancelPermissions,
}: PrescriptionProps) => {
  const theme = useTheme();
  const { id, status, orderCode = '', prescribedProducts = [], createdDate, url } = prescription?.attributes ?? {};

  const medicationCount = prescribedProducts?.length;
  const formattedDate = createdDate ? DateTime.fromISO(createdDate).toFormat('dd MMM yyyy') : '';

  const [isLoadingUrl, setIsLoadingUrl] = useState(false);

  const isViewScriptDisabled = isLoadingUrl || !url;

  const openPrescriptionUrl = async (prescriptionId: number) => {
    setIsLoadingUrl(true);
    try {
      const response = await getPrescriptionUrl(prescriptionId);

      if (response?.url) {
        window.open(response.url);
      } else {
        toast.error('Prescription not found!');
      }
    } catch (_error) {
      toast.error('Failed to retrieve prescription. Please try again.');
    } finally {
      setIsLoadingUrl(false);
    }
  };

  const openRefillPrescriptionUrl = async (prescriptionId: number) => {
    try {
      const response = await getRefillPrescriptionUrl(prescriptionId);
      if (response?.url) {
        window.open(response.url);
      } else {
        toast.error('Prescription not found!');
      }
    } catch (_error) {
      toast.error('Failed to retrieve prescription. Please try again.');
    }
  };

  return (
    <Paper elevation={1}>
      <Box
        sx={{
          padding: theme.spacing(4),
          paddingRight: { lg: theme.spacing(2) },
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            ...flexColumnToRow,
            justifyContent: { xs: 'flex-start', lg: 'space-between' },
            width: '100%',
            gap: { xs: theme.spacing(1), lg: theme.spacing(0) },
          }}
        >
          {/* Left section: Status and Order Code */}
          <Box
            sx={{
              ...flexColumnToRow,
              gap: { xs: theme.spacing(2), lg: theme.spacing(4) },
            }}
          >
            <Box sx={{ width: theme.spacing(24) }}>
              {isLoading ? (
                <Skeleton width={50} height={24} />
              ) : (
                <Chip label={status} size="small" color={status ? statusColorMap[status] : 'default'} />
              )}
            </Box>

            {isLoading ? (
              <Skeleton width={120} height={22} />
            ) : (
              <Typography variant="body1" sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                {orderCode} ({medicationCount})
              </Typography>
            )}
          </Box>

          {/* Right section: Date and View Script Button */}
          <Box
            sx={{
              ...flexColumnToRow,
              gap: { xs: theme.spacing(1), lg: theme.spacing(4) },
            }}
          >
            {isLoading ? (
              <Skeleton width={120} height={20} />
            ) : (
              <Box display="flex" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary, fontWeight: theme.typography.fontWeightMedium }}
                >
                  Created:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.primary, fontWeight: theme.typography.fontWeightMedium }}
                >
                  {formattedDate}
                </Typography>
              </Box>
            )}

            {isLoading ? (
              <Skeleton width={90} height={30} />
            ) : (
              <Tooltip
                title={
                  <>
                    Prescription is still being created.
                    <br />
                    Please try again shortly.
                  </>
                }
                placement="bottom"
                disableHoverListener={!!url}
                disableFocusListener={!!url}
                disableTouchListener={!!url}
              >
                <span>
                  <Button
                    variant="text"
                    onClick={() => id && openPrescriptionUrl(id)}
                    disabled={isViewScriptDisabled}
                    sx={{
                      flexShrink: 0,
                      marginLeft: { xs: theme.spacing(-2), lg: theme.spacing(0) },
                    }}
                  >
                    View Script
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>

      <PrescriptionMedicationsTable
        prescribedProducts={prescription?.attributes.prescribedProducts ?? []}
        isLoading={isLoading}
        onCancelProduct={onCancelProduct}
        isProductCancellable={isProductCancellable}
        hasCancelPermissions={hasCancelPermissions}
        prescriptionStatus={prescription?.attributes.status ?? PatientPrescriptionStatus.Active}
        openRefillPrescriptionUrl={openRefillPrescriptionUrl}
      />
    </Paper>
  );
};
